import { useState, type ChangeEvent } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import MDButton from "components/MDButton";
import FormField from "layouts/pages/account/components/FormField";
import { criarFornecedorFinanceiro } from "../services/fornecedores-financeiro.service";
import type { FornecedorFinanceiroApi } from "../types/contas-pagar.types";

export type NovoFornecedorDialogProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (fornecedor: FornecedorFinanceiroApi) => void;
};

export default function NovoFornecedorDialog({
  open,
  onClose,
  onCreated,
}: NovoFornecedorDialogProps) {
  const [nome, setNome] = useState("");
  const [nomeFantasia, setNomeFantasia] = useState("");
  const [documento, setDocumento] = useState("");
  const [email, setEmail] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const reset = () => {
    setNome("");
    setNomeFantasia("");
    setDocumento("");
    setEmail("");
    setErro(null);
  };

  const handleClose = () => {
    if (salvando) return;
    reset();
    onClose();
  };

  const salvar = async () => {
    const nomeTrim = nome.trim();
    if (!nomeTrim) {
      setErro("Informe o nome do fornecedor.");
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const { data, status } = await criarFornecedorFinanceiro({
        nome: nomeTrim,
        nomeFantasia: nomeFantasia.trim() || null,
        documento: documento.trim() || null,
        email: email.trim() || null,
      });
      if ((status === 200 || status === 201) && data?.id) {
        onCreated(data);
        reset();
        onClose();
        return;
      }
      setErro("Não foi possível cadastrar o fornecedor.");
    } catch (e: unknown) {
      const axiosErr = e as { response?: { data?: { error?: string } } };
      setErro(
        axiosErr.response?.data?.error ??
          (e instanceof Error ? e.message : "Erro ao cadastrar fornecedor."),
      );
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Novo fornecedor</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {erro ? <Alert severity="error">{erro}</Alert> : null}
          <FormField
            label="Nome *"
            value={nome}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setNome(e.target.value)}
            fullWidth
            autoFocus
          />
          <FormField
            label="Nome fantasia"
            value={nomeFantasia}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setNomeFantasia(e.target.value)
            }
            fullWidth
          />
          <FormField
            label="CPF/CNPJ"
            value={documento}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setDocumento(e.target.value)
            }
            fullWidth
          />
          <FormField
            label="E-mail"
            type="email"
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton variant="text" onClick={handleClose} disabled={salvando}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          onClick={() => void salvar()}
          disabled={salvando}
        >
          {salvando ? "Salvando…" : "Cadastrar"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
